<ng-template #searchActions><ng-content select="[lead-search-actions]"></ng-content></ng-template>
<ng-container *workspaceView="'full'">
<!-- `workspace-page--full` is the shared contract for fixed chrome around one
     scrolling body, and the board wants it for the same reason the list does:
     without it the columns are as tall as a guess at the viewport, and the
     space between their feet and the bottom of the window is wasted - with
     the board's own sideways scrollbar stranded in the middle of it. -->
<section class="leads-workspace-page" [class.br-card]="!embedded" [class.br-card--dense]="!embedded"
         [class.workspace-page--full]="state.view === 'list' || state.view === 'board'">
  <header *ngIf="!embedded" class="br-card-header workspace-collection-header">
    <h1 class="workspace-page__title">Leads</h1>
    <app-leads-view-switcher *ngIf="state.view === 'board' && selectedIds === null" class="rb-header-switcher" [quiet]="true"></app-leads-view-switcher>
    <button type="button" class="br-btn br-btn--primary br-btn--sm workspace-collection-add"
            *ngIf="canManageLeads" (click)="newLead()">
      <br-icon>add</br-icon> Add Lead
    </button>
  </header>
  <div [class.br-card-body]="!embedded"
       [class.workspace-page__body]="state.view === 'list' || state.view === 'board'"
       [class.workspace-page__body--contained]="state.view === 'list' || state.view === 'board'">

  <div class="rb-viewbar" *ngIf="!embedded && (state.view !== 'board' || (canManagePipeline && undoCount))">
    <app-leads-view-switcher *ngIf="state.view !== 'board' && selectedIds === null" class="rb-switcher"></app-leads-view-switcher>
    <div class="rb-rail-row" *ngIf="state.view === 'list' && !loadError">
      <app-milestone-rail class="rb-rail" [stops]="railStops" [selected]="state.columns"
                          [total]="totalCount" (selectedChange)="selectStage($event)"></app-milestone-rail>
    </div>
    <app-undo-button class="rb-undo" *ngIf="canManagePipeline && state.view === 'board' && undoCount"
                     [count]="undoCount" [busy]="undoing" [tooltip]="undoTooltip"
                     (undo)="undoLastMove()"></app-undo-button>
  </div>

  <!-- Decisions the agent is holding, on the page the leads are on. There is
       no Approvals screen to remember to visit any more. (The agent strip was
       removed from this page at the user's request in #810; the pages/ move
       carried it back in from an older copy of this template.) -->
  <app-approvals-panel *ngIf="!embedded" objectType="Lead"
                       (pendingChanged)="onPendingApprovals($event)"
                       (decided)="load()"></app-approvals-panel>

  <ng-template #leadFilters>
    <input type="search" class="br-form-control br-form-control--sm rb-search" placeholder="Search leads"
           aria-label="Search leads" [(ngModel)]="state.search" (keyup.enter)="onServerFilterChange()" />
    <ng-container *ngTemplateOutlet="searchActions"></ng-container>
    <ng-container *ngIf="!embedded"><ng-container *ngTemplateOutlet="collectionFilters; context: { showStage: state.view === 'board' }"></ng-container></ng-container>
  </ng-template>

  <!-- Board-specific controls stay at page level. In List view the same
       template is projected into app-data-table's command band below. -->
  <div class="workspace-filters br-px-0" *ngIf="state.view !== 'list'">
    <ng-container *ngTemplateOutlet="leadFilters"></ng-container>
  </div>

  <div class="br-alert br-alert--danger rb-error" *ngIf="loadError">
    <br-icon>error_outline</br-icon> {{ loadError }}
  </div>

  <!-- Board owns drag/drop and grouping; cards reuse the compact list surface and Lead summary. -->
  <div class="rb-rows" *ngIf="state.view === 'board' && !loadError" cdkDropListGroup>
    <div class="rb-row" *ngFor="let row of rows; trackBy: trackByRow"
         [class.rb-row-rep]="state.rowGroup === 'salesRep' && row.salesRepUserId != null"
         [class.rb-row-unassigned]="row.key === 'o-none'"
         >
      <!-- Assignment view (3c): the row header carries the rep's load, so a
           manager can read who to move work away from without opening a card. -->
      <div class="rb-row-title" *ngIf="row.label">
        <span class="rb-row-name">{{ row.label }}</span>
        <span class="rb-row-stats" *ngIf="state.rowGroup === 'salesRep'">{{ rowSummary(row) }}</span>
      </div>
      <!-- `cdkScrollable` is what tells CDK this strip scrolls. It caches every
           column's rectangle when a drag starts and only re-reads them when a
           REGISTERED scroll container moves, so without this the cached
           rectangles drift by the scroll offset the moment the board is
           scrolled mid-drag and the drop resolves against a column that is no
           longer there. It is also what lets a drag auto-scroll the strip:
           1620px of columns render in a 677px pane, and the columns past the
           right edge were unreachable by drag entirely. -->
      <div class="rb-board-frame">
      <div #boardViewport class="rb-board br-scroll-quiet br-focus-ring" cdkScrollable tabindex="0" role="region" aria-label="Lead stages"
           (scroll)="updateBoardOverflow(boardViewport)">
        <div #boardStage class="rb-stage" *ngFor="let column of visibleColumns"
             [style.--rb-stage-color]="stageColor(column)">
          <div class="rb-column-head">
            <span class="rb-column-title">
              <span class="rb-column-name"><span>{{ columnLabel(column) }}<ng-container *ngIf="facetFor(column) as facet"> ({{ facet.count }})</ng-container></span></span>
              <!-- Per column, because the columns are not asking the same
                   question: Prospect is a pile to work through oldest first,
                   Contract a short list you read by value. -->
              <button type="button" class="br-btn br-btn--ghost br-icon-btn br-interactive"
                      [attr.aria-pressed]="state.sortFor(column) !== 'manual'"
                      [brMenuTriggerFor]="sortMenu"
                      [matTooltip]="sortTooltip(column)"
                      [attr.aria-label]="sortTooltip(column)">
                <br-icon>sort</br-icon>
              </button>
              <ng-template #sortMenu>
                <br-menu aria-label="Sort leads">
                  <button brMenuItem *ngFor="let option of columnSorts"
                          (triggered)="setColumnSort(column, option.id)" [label]="option.label" [icon]="state.sortFor(column) === option.id ? 'check' : ''"></button>
                </br-menu>
              </ng-template>
            </span>
            <!-- Said out loud, because a sorted column will not keep a card
                 where it was dropped and silently ignoring the drop is the
                 confusing way to do that. -->
            <span class="rb-column-sorted" *ngIf="state.sortFor(column) !== 'manual'">
              {{ sortLabel(column) }}
            </span>
          </div>

          <app-board-column class="rb-column br-scroll-quiet br-focus-ring" tabindex="0"
                            [data]="column" role="list" [attr.aria-label]="columnLabel(column)"
                            (dropped)="onDrop($event, column, row.key)">
            <article appDataListCard class="rb-card br-interactive br-focus-ring" *ngFor="let item of itemsFor(column, row.key); trackBy: trackByLead"
                 cdkDrag [cdkDragData]="item" [cdkDragDisabled]="!canManagePipeline"
                 (pointerdown)="boardDragClick = false" (cdkDragStarted)="boardDragClick = true"
                 [attr.tabindex]="canManagePipeline ? 0 : null"
                 [attr.aria-label]="canManagePipeline ? (item.title || ('Lead #' + item.leadId)) + ': Enter to change stage' : null"
                 (keyup.enter)="onCardEnter($event, item)"
                 (click)="onBoardCardClick($event, item)">
              <ng-container *ngTemplateOutlet="leadSummary; context: { $implicit: item, board: true }"></ng-container>
              <div data-list-footer class="lead-summary__footer">
                <span *ngIf="stageAgeLabel(item) as ageLabel" class="lead-summary__age br-badge br-badge--neutral"
                      title="Days in current stage" [attr.aria-label]="'Days in current stage: ' + ageLabel">{{ ageLabel }}</span>
                <span *ngIf="item.nextActionType || item.nextActionDue" class="lead-summary__next"
                      [class.br-text-danger]="overdue(item)"
                      [matTooltip]="(item.nextActionType || 'Follow up') + (nextActionDateLabel(item) ? ' · ' + nextActionDateLabel(item) : '')">
                  {{ item.nextActionType || 'Follow up' }}<ng-container *ngIf="nextActionDateLabel(item) as due"> · {{ due }}</ng-container><ng-container *ngIf="overdue(item)"> · Overdue</ng-container>
                </span>
                <span class="lead-summary__tools br-inline-flex">
                  <app-map-pin [latitude]="item.lat" [longitude]="item.lng"
                               [object]="{ featureKey: 'leads', objectId: item.leadId }"
                               [popup]="{ type: 'Lead', title: item.title || ('Lead #' + item.leadId), subtitle: item.address || undefined }"></app-map-pin>
                </span>
                <ng-container *ngIf="item.salesRepUserId !== null">
                  <span *ngIf="repInitials(item.salesRepUserId!) as initials" class="lead-summary__owner br-badge br-badge--neutral"
                        role="img" [attr.aria-label]="'Assigned to ' + repLabel(item.salesRepUserId!)"
                        [matTooltip]="repLabel(item.salesRepUserId!)">{{ initials }}</span>
                </ng-container>
              </div>
              <div class="rb-card-awaiting" *ngIf="approvalsFor(item.leadId) as waiting">
                <br-icon class="rb-mini-icon">how_to_reg</br-icon>
                {{ waiting }} awaiting approval
              </div>
              <!-- Keyboard stage changes and rejected drops use the existing recovery panel. -->
              <div class="rb-move-panel" *ngIf="moveTarget?.leadId === item.leadId">
                <label class="br-form-label" [for]="'lead-stage-note-' + item.leadId">Why? (optional, kept on the history)</label>
                <input type="text" class="br-form-control br-form-control--sm"
                       [id]="'lead-stage-note-' + item.leadId" [(ngModel)]="moveNote" />
                <div class="rb-move-targets">
                  <button class="br-btn br-btn--outline-secondary br-btn--sm" *ngFor="let target of availableTargets(item)"
                          (click)="confirmMove(item, target)">{{ columnLabel(target) }}</button>
                  <button class="br-btn br-btn--link br-btn--sm" (click)="cancelMove()">Cancel</button>
                </div>
                <div class="rb-move-error" *ngIf="moveError"><br-icon>block</br-icon> {{ moveError }}</div>
              </div>

              <!-- Assignment preview (3c). Same shape as the transition preview,
                   and for the same reason: the drop is a proposal, the card has
                   not moved, and only the server's answer moves it. -->
              <div class="rb-move-panel rb-assign-panel"
                   *ngIf="assignTarget?.leadId === item.leadId">
                <div class="rb-move-heading" *ngIf="assignTo">
                  Reassign to <strong>{{ assignTo.label }}</strong>?
                  <span class="rb-assign-note">Stage stays {{ item.boardColumn }}.</span>
                </div>
                <div class="rb-move-targets" *ngIf="assignTo">
                  <button class="br-btn br-btn--primary br-btn--sm" [disabled]="assigning"
                          (click)="confirmAssign(item, assignTo)">
                    {{ assigning ? 'Reassigning…' : 'Confirm' }}
                  </button>
                  <button class="br-btn br-btn--outline-secondary br-btn--sm" [disabled]="assigning" (click)="cancelAssign()">Cancel</button>
                </div>
                <div class="rb-move-targets" *ngIf="!assignTo">
                  <button class="br-btn br-btn--outline-secondary br-btn--sm" (click)="cancelAssign()">Close</button>
                </div>
                <div class="rb-move-error" *ngIf="assignError"><br-icon>block</br-icon> {{ assignError }}</div>
              </div>
            </article>

            <app-data-list-load-more *ngIf="boardPages[column] as page"
                                     [hasMore]="hasMoreInColumn(column)" [loading]="page.loading || boardMovesPending > 0"
                                     [error]="page.error" [empty]="!itemsFor(column, row.key).length"
                                     emptyText="No leads in this stage." [progress]="page.page"
                                     (more)="loadMoreInColumn(column)" (retry)="loadMoreInColumn(column)">
            </app-data-list-load-more>
          </app-board-column>
        </div>
      </div>
      </div>
    </div>
  </div>

  <!-- List: the shared data-table in server mode (06 §5.5). Sort and paging run
       in the database over the whole filtered set — the browser holds one page.
       The shared toolbar above owns search, so the table's own box is off. -->
  <!-- No .fp-table here: that class adds overflow-x, which would become a second
       horizontal scroller around the table's own one — and sticky columns pin to
       the NEAREST scrolling ancestor, so scrolling the outer box would slide the
       pinned actions rail away. The table scrolls itself. -->
  <div class="rb-list workspace-page__body workspace-page__body--contained" *ngIf="state.view === 'list' && !loadError">
    <app-data-table class="workspace-page__panel" #listTable
                    [selectionBinding]="selectionBinding" [rowIdFn]="rowId"
                    [columns]="listColumns"
                    [fetch]="fetchList"
                    [columnPickerKey]="collectionKey"
                     [columnPickerLegacyKey]="scopedFetch ? '' : 'pipeline-list'"
                    resizableColumns
                    searchable="false"
                    [selectable]="selectable"
                    [rowActions]="selectionOnly ? null : leadRowActionsTpl"
                    [pageSize]="50"
                    [pageSizeOptions]="pageSizeOptions"
                    emptyText="No work items match this filter."
                    (selectionChange)="onListSelection($event)">
      <div dt-filters class="br-table-filter-bar">
        <ng-container *ngTemplateOutlet="leadFilters"></ng-container>
      </div>
      <!-- Two words and two icons. Which rows to export is settled in the
           dialog, where a full sentence per option fits and the counts can sit
           beside each other; it used to be a select on this band, spending the
           widest control here on a choice made once a month. -->
      <div dt-actions class="br-table-actions" *ngIf="!embedded">
        <button class="br-btn br-btn--outline-secondary br-btn--sm"
                [disabled]="exporting || !listPageCount" (click)="openExport()">
          <br-icon>download</br-icon>
          {{ exporting ? 'Reading…' : 'Export' }}
        </button>
        <!-- The shared pair. Leads is the one table whose import endpoints
             gate on Import.Run rather than Import.Manage; the shared component
             knows that, so this page no longer has to. -->
        <app-table-transfer-actions *ngIf="selectedIds === null" table="leads" title="leads"
                                    (changed)="onTransferChanged()"></app-table-transfer-actions>
        <button type="button"
                class="br-btn br-btn--outline-secondary br-btn--sm br-icon-btn"
                *ngIf="selectedIds === null && canDeleteAllData"
                [brMenuTriggerFor]="leadMoreActions"
                aria-label="Lead data actions"
                [disabled]="deletingAll">
          <br-icon>more_horiz</br-icon>
        </button>
        <ng-template #leadMoreActions>
          <br-menu aria-label="Lead data actions">
            <button brMenuItem (triggered)="deleteAllLeads()" label="Delete All" icon="delete_sweep"></button>
          </br-menu>
        </ng-template>
      </div>
      <div dt-actions class="br-table-export-error" *ngIf="!embedded && exportError">
        <br-icon>block</br-icon> {{ exportError }}
      </div>
      <button dt-bulk-actions
              type="button"
              class="br-btn br-btn--outline-danger br-btn--sm"
              *ngIf="!embedded && canManageLeads"
              (click)="deactivateSelected()">
        <br-icon>block</br-icon>
        Deactivate
      </button>
      <div dt-bulk-actions><ng-container *ngTemplateOutlet="bulkActions; context: { $implicit: listTable.selection }"></ng-container></div>
      <ng-template *ngFor="let column of listColumns" [dtCell]="column.id" let-row>
        <ng-container *ngIf="cellTemplate(column.id) as custom; else standardCell">
          <ng-container *ngTemplateOutlet="custom; context: { $implicit: row }"></ng-container>
        </ng-container>
        <ng-template #standardCell><ng-container *ngTemplateOutlet="leadCell; context: { $implicit: row, column: column.id }"></ng-container></ng-template>
      </ng-template>
    </app-data-table>


  </div>

  </div>

</section>
</ng-container>
<ng-container *workspaceView="'compact'">
  <section class="workspace-page workspace-page--full">
    <h1 *ngIf="!embedded" class="workspace-page__title">Leads</h1>
    <app-data-list #compactList (selectionChange)="onListSelection($event)" [contextKey]="scopedFetch ? collectionKey : ''" [selectable]="selectable" [fetch]="fetchItems"
                   [selectionBinding]="selectionBinding" [trackByFn]="trackByLead"
                   [search]="state.search" (searchChange)="onCompactSearch($event)"
                   [sortOptions]="compactSortOptions"
                   (revealMissed)="onCompactRevealMissed()">
      <div list-selection-actions><ng-container *ngTemplateOutlet="bulkActions; context: { $implicit: compactList.selection }"></ng-container></div>
      <span *ngIf="!embedded" list-selection-actions matTooltip="Coming soon">
        <button type="button" class="br-btn br-btn--outline-secondary br-btn--sm" disabled>Bulk actions</button>
      </span>
      <span list-filters><ng-container *ngTemplateOutlet="searchActions"></ng-container></span>
      <div *ngIf="!embedded" list-filters class="br-flex br-flex-wrap br-items-center br-gap-2">
        <ng-container *ngTemplateOutlet="collectionFilters; context: { showStage: true }"></ng-container>
      </div>
      <!-- The map asked for a lead these rules were hiding. -->
      <span *ngIf="revealNotice" list-filters class="br-text-muted" role="status">
        {{ revealNotice }}
        <button *ngIf="canRestoreRevealFilters" type="button"
                class="br-btn br-btn--link br-btn--sm"
                (click)="restoreRevealedFilters()">Undo</button>
      </span>
      <ng-template dataListItem let-row>
        <ng-container *ngTemplateOutlet="leadSummary; context: { $implicit: row, board: false }"></ng-container>
      </ng-template>
      <ng-container *ngIf="!selectionOnly && !rowExtras">
        <ng-template dataListDetail let-row>
          <ng-container *ngTemplateOutlet="leadDetail; context: { $implicit: row }"></ng-container>
        </ng-template>
      </ng-container>
    </app-data-list>
  </section>
</ng-container>

<!-- Dock list items lazily mount inline details; Board cards use the routed page. -->
<ng-template #leadDetail let-row>
  <app-lead-page #compactLeadPage [inline]="true" [showEditAction]="false" [leadId]="row.leadId"
                 (profileLoaded)="updateCompactLead(row, $event)">
  </app-lead-page>
</ng-template>

<ng-template #collectionFilters let-showStage="showStage">
  <button *ngIf="showStage" type="button" class="br-table-filter-trigger br-interactive" aria-label="Stage"
          [class.br-interactive--selected]="state.columns.length > 0 || stageTrigger.isOpen()" #stageTrigger="brPopoverTrigger"
          [class.br-table-filter-trigger--active]="state.columns.length > 0"
          [brPopoverFor]="leadStageMenu" brPopoverLabel="Stage">
    <span>{{ stageFilterLabel }}</span>
    <br-icon aria-hidden="true">keyboard_arrow_down</br-icon>
  </button>
  <ng-template #leadStageMenu>
    <div class="br-table-filter-choices" role="group" aria-label="Stage">
    <button type="button" class="br-btn br-btn--sm br-interactive br-table-filter-choice" role="checkbox"
            [attr.aria-checked]="state.columns.length === 0"
            [class.br-interactive--selected]="state.columns.length === 0"
            (click)="selectStage(null)">
      <br-icon aria-hidden="true">{{ state.columns.length === 0 ? 'check_box' : 'check_box_outline_blank' }}</br-icon>
      All stages
    </button>
    <button type="button" class="br-btn br-btn--sm br-interactive br-table-filter-choice" role="checkbox" *ngFor="let stage of leadStages"
            [attr.aria-checked]="state.columns.includes(stage.code)"
            [class.br-interactive--selected]="state.columns.includes(stage.code)"
            (click)="selectStage(stage.code)">
      <br-icon aria-hidden="true">{{ state.columns.includes(stage.code) ? 'check_box' : 'check_box_outline_blank' }}</br-icon>
      {{ stage.name }}
    </button>
      </div>
  </ng-template>
  <button *ngIf="territories.length" type="button" class="br-table-filter-trigger br-interactive" aria-label="Territory"
          [class.br-interactive--selected]="territorySelection !== null || territoryTrigger.isOpen()" #territoryTrigger="brMenuTrigger"
          [class.br-table-filter-trigger--active]="territorySelection !== null"
          [brMenuTriggerFor]="leadTerritoryMenu">
    <span>{{ compactTerritoryLabel }}</span>
    <br-icon aria-hidden="true">keyboard_arrow_down</br-icon>
  </button>
  <ng-template #leadTerritoryMenu>
    <br-menu aria-label="Territory filter">
      <button type="button" brMenuItem [current]="territorySelection === null"
              (triggered)="territorySelection = null; onServerFilterChange()" label="All territories"></button>
      <button *ngIf="canFilterMyTerritories" type="button" brMenuItem
              [current]="territorySelection === 'mine'"
              (triggered)="territorySelection = 'mine'; onServerFilterChange()" label="My territories"></button>
      <button type="button" brMenuItem *ngFor="let territory of territories"
              [current]="territorySelection === territory.id"
              (triggered)="territorySelection = territory.id; onServerFilterChange()" [label]="territory.name"></button>
    </br-menu>
  </ng-template>
  <label class="lead-filters__assigned br-inline-flex br-items-center br-gap-2">
    <input type="checkbox" class="br-form-check-input" [(ngModel)]="state.assignedToMeOnly"
           (ngModelChange)="onServerFilterChange()" />
    <span>Assigned to me</span>
  </label>
  <app-data-filter [fields]="filterFields" [values]="state.ruleFilters"
                   (change)="state.ruleFilters = $event; onServerFilterChange()"></app-data-filter>
</ng-template>

<ng-template #leadSummary let-row let-board="board">
  <div class="lead-summary" [class.lead-summary--board]="board">
  <div class="lead-summary__heading br-flex br-justify-between br-gap-2">
    <div class="br-flex-grow-1 br-min-w-0">
      <div class="lead-summary__identity br-flex br-flex-wrap br-items-center br-gap-2">
        <button *ngIf="board" type="button" class="br-btn br-btn--ghost br-interactive rb-open-btn lead-summary__name" (click)="openDetail(row, $event)">
          {{ row.title || ('Lead #' + row.leadId) }}
        </button>
        <button *ngIf="!board" type="button" class="map-locate-name"
                [appShowOnMap]="{ featureKey: 'leads', objectId: row.leadId }"
                [latitude]="row.lat" [longitude]="row.lng"
                [popup]="{ type: 'Lead', title: row.title || ('Lead #' + row.leadId), subtitle: row.address || undefined }"
                #nameOnMap="showOnMap" [title]="nameOnMap.located ? 'Show on map' : ''">
          <strong class="lead-summary__name">{{ row.title || ('Lead #' + row.leadId) }}</strong>
        </button>
        <ng-container *ngIf="selectionOnly"><ng-container *ngTemplateOutlet="selectionStatus; context: { $implicit: row }"></ng-container></ng-container>
        <span *ngIf="!board" class="rb-stage-badge lead-summary__stage" [style.color]="stageColor(row.boardColumnCode)">
          {{ row.boardColumn }}</span>
      </div>
    </div>
    <ng-container *ngIf="scopedFetch && !board"><ng-container *ngTemplateOutlet="leadRowActionsTpl; context: { $implicit: row }"></ng-container></ng-container>
    <button *ngIf="!embedded && canManageLeads && !board" type="button" class="lead-summary__edit br-btn br-btn--ghost br-icon-btn br-interactive"
            aria-label="Edit lead" matTooltip="Edit"
            [disabled]="compactList?.expandedItem === row && !!compactLeadPage && !compactLeadPage.isView"
            (click)="editCompactLead(row)">
      <br-icon>edit</br-icon>
    </button>
  </div>
  <div class="lead-summary__meta">
    <app-map-pin *ngIf="!board" [latitude]="row.lat" [longitude]="row.lng"
                 [object]="{ featureKey: 'leads', objectId: row.leadId }"
                 [popup]="{ type: 'Lead', title: row.title || ('Lead #' + row.leadId), subtitle: row.address || undefined }"></app-map-pin>
    <span class="lead-summary__address">
      <ng-container *ngIf="board; else fullAddress"><ng-container *ngIf="row.businessTypes?.length">{{ typeLabels(row.businessTypes) }} · </ng-container>{{ boardLocation(row) }}</ng-container>
      <ng-template #fullAddress>
        <span *ngIf="row.address || row.city || row.state || row.postalCode; else noAddress">
          {{ row.address }}<ng-container *ngIf="row.city">{{ row.address ? ', ' : '' }}{{ row.city }}</ng-container><ng-container *ngIf="row.state">{{ row.address || row.city ? ', ' : '' }}{{ row.state }}</ng-container><ng-container *ngIf="row.postalCode"> {{ row.postalCode }}</ng-container>
        </span>
        <ng-template #noAddress><span class="lead-summary__missing">No address</span></ng-template>
      </ng-template>
      <span *ngIf="!board && row.businessTypes?.length" class="br-text-muted"> · {{ typeLabels(row.businessTypes) }}</span>
    </span>
  </div>
  <div *ngIf="!board && !rowExtras && (row.nextActionType || row.nextActionDue)" class="lead-summary__next br-flex br-flex-wrap br-items-center br-gap-2">
    <span class="lead-summary__task">{{ row.nextActionType || 'Follow up' }}</span>
    <span *ngIf="row.nextActionDue" [class.br-text-danger]="overdue(row)">{{ row.nextActionDue | date:'MMM d, y' }}</span>
  </div>
  <ng-container *ngIf="!board"><ng-container *ngTemplateOutlet="rowExtras; context: { $implicit: row }"></ng-container></ng-container>
  <div *ngIf="!board" class="lead-summary__ai">
    <br-icon aria-hidden="true">auto_awesome</br-icon>
    <span>AI summary · Coming soon</span>
  </div>
  </div>
</ng-template>

<ng-template #leadCell let-row let-column="column">
  <ng-container [ngSwitch]="column">
    <ng-container *ngSwitchCase="'title'">
        <span *ngIf="selectionOnly; else leadTitleLink">{{ row.title || ('Lead #' + row.leadId) }} <ng-container *ngTemplateOutlet="selectionStatus; context: { $implicit: row }"></ng-container></span>
        <ng-template #leadTitleLink><button type="button" class="br-btn br-btn--ghost br-interactive rb-open-btn" (click)="openDetail(row)">
          {{ row.title || ('Lead #' + row.leadId) }}</button></ng-template>

    </ng-container>
    <ng-container *ngSwitchCase="'boardColumn'">
        <span class="rb-stage-badge" [style.color]="stageColor(row.boardColumnCode)">
          {{ row.boardColumn }}</span>

    </ng-container>
    <ng-container *ngSwitchCase="'address'">{{ row.address || '—' }}
    </ng-container>
    <ng-container *ngSwitchCase="'businessTypes'">{{ typeLabels(row.businessTypes) || '—' }}
    </ng-container>
    <ng-container *ngSwitchCase="'discoveredAt'">
        {{ row.discoveredAt | date:'MMM d, y' }}

    </ng-container>
    <ng-container *ngSwitchCase="'origin'">{{ row.origin }}
    </ng-container>
    <ng-container *ngSwitchCase="'sources'">{{ sourceLabel(row) }}
    </ng-container>
    <ng-container *ngSwitchCase="'salesRep'">{{ row.salesRepUserId ?? '—' }}
    </ng-container>
    <ng-container *ngSwitchCase="'score'">{{ row.qualificationScore ?? '—' }}
    </ng-container>
    <ng-container *ngSwitchCase="'contribution'">
        {{ row.contributionBase != null ? (row.contributionBase | currency:'USD':'symbol':'1.0-0') : '—' }}

    </ng-container>
    <ng-container *ngSwitchCase="'routeFit'">{{ row.routeFitStatus ?? '—' }}
    </ng-container>
    <ng-container *ngSwitchCase="'nextAction'">
        <span [class.rb-overdue]="overdue(row)">
          {{ row.nextActionType ?? '—' }}<ng-container *ngIf="row.nextActionDue"> · {{ row.nextActionDue | date:'MMM d' }}</ng-container>
        </span>

    </ng-container>
    <ng-container *ngSwitchCase="'probability'">
        {{ row.probability != null ? (row.probability | percent:'1.0-0') : '—' }}

    </ng-container>
    <ng-container *ngSwitchDefault>{{ row[column] ?? '—' }}</ng-container>
  </ng-container>
</ng-template>

    <ng-template #leadRowActionsTpl let-row>
      <button type="button" mat-icon-button
              matTooltip="View" aria-label="View lead"
              (click)="openDetail(row)">
        <br-icon>visibility</br-icon>
      </button>
      <button type="button" mat-icon-button
              *ngIf="!embedded && canManageLeads"
              matTooltip="Edit" aria-label="Edit lead"
              (click)="editLead(row)">
        <br-icon>edit</br-icon>
      </button>
      <button type="button" mat-icon-button
              *ngIf="(!embedded && canManageLeads) || rowActionMenu"
              [brMenuTriggerFor]="rowActionMenu || leadRowMenu"
              [brMenuTriggerData]="{ $implicit: row }"
              matTooltip="Actions" aria-label="Lead actions">
        <br-icon>more_vert</br-icon>
      </button>
      <ng-template #leadRowMenu let-row>
        <br-menu aria-label="Lead actions">
          <button *ngIf="!embedded" type="button" brMenuItem (triggered)="deactivateLead(row)" label="Deactivate" icon="block"></button>
        </br-menu>
      </ng-template>
    </ng-template>
